<template>
    <div
        class="flex h-auto w-full flex-col items-start justify-start space-y-6"
    >
        <NumberField
            v-model="score"
            name="esg_score"
            label="campaign_marketing_esg_score"
            help="help_campaign_marketing_esg_score"
            unit="%"
            :min="0"
            :max="100"
            :maximum-fraction-digits="1"
            :allow-null="true"
        />
        <EditorField
            v-model="description"
            name="esg_description"
            label="campaign_marketing_esg_description"
            help="help_campaign_marketing_esg_description"
            variant="minimal"
            class="w-full"
        />
        <RepeatableFieldGroup
            v-model="cards"
            name-prefix="esg_cards"
            :fields="esgCardFields"
        />
        <AccordionBasic
            title="campaign_marketing_esg_sdgs"
            class="mb-2 w-full"
            :open="false"
        >
            <RepeatableFieldGroup
                v-model="sdgs"
                name-prefix="esg_sdgs"
                :fields="esgSdgFields"
            />
        </AccordionBasic>
        <AccordionBasic
            title="campaign_marketing_esg_document"
            class="mb-2 w-full"
            :open="false"
        >
            <DocumentUpload
                v-model="document"
                name="esg_document"
                label="campaign_marketing_esg_document_file"
                help="help_campaign_marketing_esg_document_file"
            />
        </AccordionBasic>
    </div>
</template>

<script setup lang="ts">
import AccordionBasic from '@src/components/AccordionBasic.vue'
import DocumentUpload from '@src/components/DocumentUpload.vue'
import EditorField from '@src/components/EditorField.vue'
import NumberField from '@src/components/NumberField.vue'
import RepeatableFieldGroup, {
    type RepeatableFieldSchema,
} from '@src/components/RepeatableFieldGroup.vue'
import SelectField from '@src/components/SelectField.vue'
import TextField from '@src/components/TextField.vue'
import { sdgGoals } from '@src/const'
import {
    CampaignEsgSdg,
    CampaignMarketingTitleDescription,
    ImageInterface,
} from '@src/types'
import { useVModels } from '@vueuse/core'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    score: number | null
    description: string | null
    cards: CampaignMarketingTitleDescription[]
    sdgs: CampaignEsgSdg[]
    document: ImageInterface | null
}>()

const emit = defineEmits<{
    (e: 'update:score', value: number | null): void
    (e: 'update:description', value: string | null): void
    (e: 'update:cards', value: CampaignMarketingTitleDescription[]): void
    (e: 'update:sdgs', value: CampaignEsgSdg[]): void
    (e: 'update:document', value: ImageInterface | null): void
}>()

const { score, description, cards, sdgs, document } = useVModels(props, emit)

const { t } = useI18n()

const esgCardFields: RepeatableFieldSchema[] = [
    {
        key: 'title',
        component: TextField,
        label: 'campaign_marketing_esg_card_title',
        help: 'help_campaign_marketing_esg_card_title',
        default: '',
    },
    {
        key: 'description',
        component: EditorField,
        label: 'campaign_marketing_esg_card_description',
        help: 'help_campaign_marketing_esg_card_description',
        default: '',
        props: { variant: 'minimal' },
    },
]

const esgSdgFields = computed<RepeatableFieldSchema[]>(() => [
    {
        key: 'sdg_number',
        component: SelectField,
        label: 'campaign_marketing_esg_sdg_number',
        help: 'help_campaign_marketing_esg_sdg_number',
        default: null,
        props: {
            options: sdgGoals.map((goal) => ({
                label: `${goal.number} – ${t(goal.title)}`,
                value: goal.number,
            })),
        },
    },
    {
        key: 'description',
        component: EditorField,
        label: 'campaign_marketing_esg_sdg_description',
        help: 'help_campaign_marketing_esg_sdg_description',
        default: '',
        props: { variant: 'minimal' },
    },
])
</script>
